// Helpers/GetCashflowClientsForUserAndAdvisers.js
import {
  collection,
  getDoc,
  getDocs,
  doc,
  query,
  where,
} from "firebase/firestore";
import { firestore as db } from "../../../../backend/firebaseconfig";
import { getAllCashflowEntries } from "../FirebaseHelpers/FirebaseHelpers";
import { getCashflowClientsForUser } from "./GetCashflowClientsForUser";
import dayjs from "dayjs";

const tidy = (s) =>
  String(s || "")
    .trim()
    .replace(/\s+/g, "");

const nameFromDetails = (details) =>
  Array.isArray(details)
    ? details
        .slice(0, 2)
        .map((d) => d?.name)
        .filter(Boolean)
        .join(" & ") || "Unnamed Client"
    : "Unnamed Client";

/**
 * Returns:
 *  - current user's clients (ownerRole=userRole, ownerDocId=userEmail)
 *  - + each adviser’s clients found under:
 *      Authorized Members / Advisers / Data / <docId>
 *    where <docId> is either the email itself (exact doc) OR the doc whose `email` field equals the email.
 * Adds ownerRole/ownerDocId/ownerEmail for reliable navigation.
 */
export const getCashflowClientsForUserAndAdvisers = async ({
  userRole,
  userEmail,
  adviserEmails = [],
}) => {
  const safeUserRole = tidy(userRole);
  const safeUserEmail = tidy(userEmail);
  const targets = Array.from(
    new Set((adviserEmails || []).map(tidy).filter(Boolean))
  );

  // --- 1) Own clients
  const ownRaw = await getCashflowClientsForUser(safeUserRole, safeUserEmail);
  const own = ownRaw.map((row) => ({
    ...row,
    ownerRole: safeUserRole,
    ownerDocId: safeUserEmail,
    ownerEmail: safeUserEmail,
  }));

  // --- 2) Advisers (robust lookups per email)
  const colRef = collection(db, "Authorized Members", "Advisers", "Data");
  const adviserRows = [];
    const normBool = (v) => v === true || v === "true" || v === 1 || v === "1";

    for (const email of targets) {
      let parentDoc = null;
      let parentData = null;

      // Try exact doc id first
      const idRef = doc(colRef, email);
      const idSnap = await getDoc(idRef);
      if (idSnap.exists()) {
        parentDoc = idSnap;
        parentData = idSnap.data() || {};
        // console.log("[adviser:docId] ", email);
      } else {
        // Fallback: match by email field exactly
        const q = query(colRef, where("email", "==", email));
        const qs = await getDocs(q);
        if (!qs.empty) {
          parentDoc = qs.docs[0];
          parentData = parentDoc.data() || {};
          // console.log("[adviser:emailField] ", email, "->", parentDoc.id);
        } else {
          // console.warn("[adviser:miss]  no doc for email:", email);
          continue;
        }
      }

      const parentDocId = parentDoc.id; // use THIS for pathing
      const parentEmail = tidy(parentData.email) || parentDocId; // display email
      const tradingName =
        parentData.tradingName || "Rosemount Financial Solutions (IFA) Ltd";
      const primaryColor = parentData.primaryColor || "081e56";
      const regulatoryText = parentData.regulatoryText || "";

      // Load entries under the adviser doc id
      const entries = await getAllCashflowEntries("Advisers", parentDocId);

      for (const entry of entries) {
        adviserRows.push({
          id: entry.uid,
          role: "Advisers",
          name: nameFromDetails(entry.details),
          rawDate: entry.createdAt || null,
          date: entry.createdAt
            ? dayjs(entry.createdAt).format("DD/MM/YYYY")
            : "Unknown",
          updatedOn: entry.updatedOn
            ? dayjs(entry.updatedOn).format("DD/MM/YYYY")
            : "",
          createdBy: parentEmail,
          tradingName,
          primaryColor,
          regulatoryText: regulatoryText,
          isScenario: normBool(entry?.isScenario),
          parentDocId: entry?.parentDocId || "",
          scenarios: Array.isArray(entry?.scenarios)
            ? entry.scenarios
                .map((s) => ({ id: s?.id, name: s?.name }))
                .filter((s) => !!s.id)
            : [],
        });
      }
    }

  // --- 3) Combine, de-dupe by owner segments + id, sort
  const seen = new Set();
  const combined = [];
  for (const row of [...own, ...adviserRows]) {
    const key = `${row.ownerRole}|${row.ownerDocId}|${row.id}`;
    if (!seen.has(key)) {
      seen.add(key);
      combined.push(row);
    }
  }

  combined.sort((a, b) =>
    String(b.updatedOn || b.date || "").localeCompare(
      String(a.updatedOn || a.date || "")
    )
  );

  return combined;
};
